/**
 * Master Marketing Calendar — every client's activities in one place.
 *
 * Admins land here from the sidebar. The page reuses the same MonthGrid +
 * STATUS_META + ActivityPill components as the per-client calendar, plus
 * filters for client and (optionally) property. Clicking an activity
 * jumps to the per-client calendar with the activity dialog open, keeping
 * a single source of truth for editing/sign-off flows.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  CalendarDays, ChevronLeft, ChevronRight, Loader2, Maximize2, Minimize2,
  Filter, ExternalLink,
} from "lucide-react";
import {
  MonthGrid,
} from "@/pages/ClientCalendarPage";
import {
  addMonths, startOfMonth, isoDate,
} from "@/components/calendar/calendarUtils";
import { PLATFORMS, PlatformIcon } from "@/components/PlatformIcon";

export default function MasterCalendarPage() {
  const navigate = useNavigate();

  const [anchor, setAnchor] = useState(() => startOfMonth(new Date()));
  const monthA = anchor;
  const monthB = addMonths(anchor, 1);

  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandAll, setExpandAll] = useState(false);
  const [expandedWeeks, setExpandedWeeks] = useState(() => new Set());

  const [clientFilter, setClientFilter] = useState("_all");
  const [propertyFilter, setPropertyFilter] = useState("_all");
  const [platformFilter, setPlatformFilter] = useState("_all");

  const rangeStart = useMemo(() => isoDate(monthA), [monthA]);
  const rangeEnd = useMemo(() => isoDate(addMonths(anchor, 2)), [anchor]);

  // Bootstrap: load all clients (admin endpoint) for the dropdown.
  useEffect(() => {
    api.get("/clients").then((r) => {
      const rows = Array.isArray(r.data) ? r.data : (r.data?.clients || []);
      // Sort alphabetically by display name so the dropdown is browsable.
      const named = rows.map((c) => ({
        ...c,
        _display: c.company_name || c.company || c.name || c.client_id,
      }));
      named.sort((a, b) => (a._display || "").localeCompare(b._display || ""));
      setClients(named);
    }).catch(() => setClients([]));
  }, []);

  // Properties dropdown only meaningful when a single client is selected.
  useEffect(() => {
    if (clientFilter === "_all") { setProperties([]); setPropertyFilter("_all"); return; }
    api.get(`/clients/${clientFilter}/business-units`)
      .then((r) => setProperties(r.data || []))
      .catch(() => setProperties([]));
    setPropertyFilter("_all");
  }, [clientFilter]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start: rangeStart, end: rangeEnd };
      if (clientFilter !== "_all") params.client_id = clientFilter;
      if (propertyFilter !== "_all") params.business_unit_id = propertyFilter;
      if (platformFilter !== "_all") params.platform = platformFilter;
      const { data } = await api.get("/calendar/activities", { params });
      // Enrich with client_name + business_unit_name for richer tooltips.
      const clientMap = Object.fromEntries(clients.map(
        (c) => [c.client_id, c._display || c.company_name || c.company || c.name || c.client_id]));
      const buMap = Object.fromEntries(properties.map(
        (p) => [p.business_unit_id, p.name]));
      const enriched = (data?.activities || []).map((a) => ({
        ...a,
        client_name: clientMap[a.client_id],
        business_unit_name: buMap[a.business_unit_id],
      }));
      setActivities(enriched);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load calendar");
      setActivities([]);
    } finally { setLoading(false); }
  }, [rangeStart, rangeEnd, clientFilter, propertyFilter, platformFilter, clients, properties]);

  useEffect(() => { reload(); }, [reload]);

  const byDay = useMemo(() => {
    const m = {};
    for (const a of activities) {
      const key = (a.scheduled_at || "").slice(0, 10);
      if (!key) continue;
      (m[key] = m[key] || []).push(a);
    }
    for (const k of Object.keys(m)) {
      m[k].sort((x, y) => (x.scheduled_at || "").localeCompare(y.scheduled_at || ""));
    }
    return m;
  }, [activities]);

  const toggleWeek = (weekKey) => {
    setExpandedWeeks((prev) => {
      const next = new Set(prev);
      if (next.has(weekKey)) next.delete(weekKey); else next.add(weekKey);
      return next;
    });
  };

  // Jumps to the per-client calendar with the activity dialog deep-linked.
  const openActivity = (a) => {
    navigate(`/clients/${a.client_id}/calendar?activity=${a.activity_id}`);
  };

  return (
    <section className="space-y-4" data-testid="master-calendar-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-sm grid place-items-center bg-[#002FA7]/10">
            <CalendarDays className="h-4 w-4 text-[#002FA7]" />
          </span>
          <div>
            <h2 className="font-display font-bold text-2xl tracking-tight">
              Master Calendar
            </h2>
            <p className="text-[12px] text-zinc-500">
              Every client&apos;s scheduled marketing activity — filter by client
              or property, click any pill to jump to the activity.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button size="sm" variant="outline" className="rounded-sm h-8"
            onClick={() => setExpandAll((v) => !v)}
            data-testid="master-calendar-expand-all">
            {expandAll
              ? <><Minimize2 className="h-3.5 w-3.5 mr-1" /> Compact view</>
              : <><Maximize2 className="h-3.5 w-3.5 mr-1" /> Expand all</>}
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm h-8"
            onClick={() => setAnchor(addMonths(anchor, -1))}
            data-testid="master-calendar-prev-month">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm h-8"
            onClick={() => setAnchor(startOfMonth(new Date()))}
            data-testid="master-calendar-today">
            Today
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm h-8"
            onClick={() => setAnchor(addMonths(anchor, 1))}
            data-testid="master-calendar-next-month">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="rounded-sm">
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center gap-1.5 text-zinc-500 text-[11px] font-mono uppercase tracking-wider">
            <Filter className="h-3 w-3" /> Filter
          </div>
          <div className="min-w-[200px]">
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="filter-client">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All clients</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.client_id} value={c.client_id}>
                    {c._display}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[200px]">
            <Select value={propertyFilter} onValueChange={setPropertyFilter}
              disabled={clientFilter === "_all"}>
              <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="filter-property">
                <SelectValue placeholder="All properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All properties</SelectItem>
                {properties.map((p) => (
                  <SelectItem key={p.business_unit_id} value={p.business_unit_id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="filter-platform">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All platforms</SelectItem>
                {PLATFORMS.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    <span className="inline-flex items-center gap-2">
                      <PlatformIcon platform={p.key} size={14} /> {p.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {clientFilter !== "_all" && (
            <Button size="sm" variant="ghost" className="h-7 text-[11px] ml-auto"
              onClick={() => navigate(`/clients/${clientFilter}/calendar`)}
              data-testid="open-client-calendar">
              <ExternalLink className="h-3 w-3 mr-1" />
              Open client calendar
            </Button>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <Card className="rounded-sm">
          <CardContent className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading activities…
          </CardContent>
        </Card>
      ) : activities.length === 0 ? (
        <Card className="rounded-sm">
          <CardContent className="p-8 text-center text-sm text-zinc-500">
            <CalendarDays className="h-8 w-8 mx-auto text-zinc-300 mb-2" />
            No activities scheduled in this window.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <MonthGrid
            monthDate={monthA} byDay={byDay} isAdmin={false}
            expandAll={expandAll}
            expandedWeeks={expandedWeeks} onToggleWeek={toggleWeek}
            onActivityClick={openActivity} />
          <MonthGrid
            monthDate={monthB} byDay={byDay} isAdmin={false}
            expandAll={expandAll}
            expandedWeeks={expandedWeeks} onToggleWeek={toggleWeek}
            onActivityClick={openActivity} />
        </div>
      )}
    </section>
  );
}
